import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'

import { AccordionBanner } from '@/components/accordion'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { USER_DOCUMENT_RENEWAL_REMINDER_QUERY } from './queries'
import {
  buildUserDocumentRenewalSections,
  formatUserDocumentRenewalStatus,
} from './user-document-renewal'
import { useSectionPalette } from './useSectionPalette'

function isDefined<T>(value: T | null | undefined): value is T {
  return value != null
}

export const UserDocumentRenewalReminderBanner: React.FC = () => {
  const user = useUser()
  const { hasAnyPermissions } = usePermissions()
  const canManageOwnDocuments = hasAnyPermissions([
    PERMISSIONS.users.change.userdocument,
    PERMISSIONS.users.delete.userdocument,
  ])

  const translations = useTranslate({
    expiredDays: ['header.user-document-renewal.expired-days', { count: 0 }],
    expiresInDays: [
      'header.user-document-renewal.expires-in-days',
      { count: 0 },
    ],
    expiringSoonTitle: 'header.user-document-renewal.expiring-soon',
    overdueTitle: 'header.user-document-renewal.overdue',
  })

  const sectionPalette = useSectionPalette()

  const {
    data: ownData,
    loading: ownLoading,
    error: ownError,
  } = useQuery(USER_DOCUMENT_RENEWAL_REMINDER_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !canManageOwnDocuments,
    variables: { user: user.id },
  })

  const {
    data: responsibleData,
    loading: responsibleLoading,
    error: responsibleError,
  } = useQuery(USER_DOCUMENT_RENEWAL_REMINDER_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !canManageOwnDocuments,
    variables: { responsibleUser: user.id },
  })

  const documents = [
    ...(ownData?.allUserDocuments?.edges
      .map(edge => edge?.node)
      .filter(isDefined) ?? []),
    ...(responsibleData?.allUserDocuments?.edges
      .map(edge => edge?.node)
      .filter(isDefined) ?? []),
  ]

  const sections = buildUserDocumentRenewalSections({ documents }).map(
    section => {
      const palette = sectionPalette[section.status]
      const title =
        section.status === 'overdue'
          ? translations.overdueTitle
          : translations.expiringSoonTitle

      return {
        id: section.id,
        count: section.items.length,
        defaultOpen: false,
        icon: palette.icon,
        title,
        style: palette.style,
        headerStyle: palette.headerStyle,
        titleStyle: palette.titleStyle,
        badgeStyle: palette.badgeStyle,
        chevronStyle: palette.chevronStyle,
        items: section.items.map(item => {
          const status = formatUserDocumentRenewalStatus({
            expirationDate: item.expirationDate,
          })
          const metaText =
            status.key === 'header.user-document-renewal.expired-days'
              ? translations.expiredDays({ count: status.value })
              : translations.expiresInDays({ count: status.value })

          return {
            href: `/users/${item.userId}`,
            itemKey: item.id,
            metaStyle: palette.metaStyle,
            metaText,
            primaryText: item.userName,
            secondaryText: item.documentName,
          }
        }),
      }
    }
  )

  if (
    !canManageOwnDocuments ||
    ownLoading ||
    responsibleLoading ||
    ownError ||
    responsibleError ||
    sections.length === 0
  ) {
    return null
  }

  return <AccordionBanner sections={sections} />
}
